(() => {
    if (customElements.get('rebuy-cx-slider-init')) {
        return;
    }

    customElements.define('rebuy-cx-slider-init', class extends HTMLElement {
        domReadyBound = false;

        /** True after Rebuy.init for this element instance (reset on disconnect when cart morphs). */
        #initCalledForThisElement = false;

        /** True once the "hidden" class has been removed for this element instance (reset on disconnect). */
        #sliderRevealed = false;

        /** @type {number} */
        #initRetryTimeout = 0;

        #containerObserver = null;
        #documentObserver = null;
        #refreshFrame = 0;
        #loadBound = false;
        #lastSliderTrack = null;
        #lastSliderCardCount = -1;
        #lastSliderArrows = [];
        #lastSliderToggles = [];

        constructor() {
            super();
            this.initRebuy = this.initRebuy.bind(this);
            this.initRebuySlider = this.initRebuySlider.bind(this);
            this.tryInitRebuySlider = this.tryInitRebuySlider.bind(this);
            this.observeRebuy = this.observeRebuy.bind(this);
            this.scheduleRefresh = this.scheduleRefresh.bind(this);
        }

        /**
         * Rebuy mount is replaced when the mini-cart section morphs; an empty container needs init again.
         * @param {string} widgetId
         * @returns {boolean}
         */
        #rebuyMountNeedsInit(widgetId) {
            const container = document.querySelector(`[data-rebuy-id="${widgetId}"]`);
            if (!container) return true;
            return !container.querySelector('.rebuy-product-block, .custom-container__card--product');
        }

        connectedCallback() {
            if (!this.dataset.rebuyWidgetId) {
                return;
            }

            if (document.readyState === 'loading') {
                this.domReadyBound = true;
                document.addEventListener('DOMContentLoaded', this.observeRebuy, { once: true });
            } else {
                this.observeRebuy();
            }
            document.addEventListener('cart-drawer:open', this.initRebuy);
            document.addEventListener('rebuy.ready', this.initRebuySlider);
        }

        disconnectedCallback() {
            if (this.domReadyBound) {
                document.removeEventListener('DOMContentLoaded', this.observeRebuy);
                this.domReadyBound = false;
            }
            document.removeEventListener('cart-drawer:open', this.initRebuy);
            document.removeEventListener('rebuy.ready', this.initRebuySlider);
            if (this.#loadBound) {
                window.removeEventListener('load', this.scheduleRefresh);
                this.#loadBound = false;
            }
            this.#containerObserver?.disconnect();
            this.#containerObserver = null;
            this.#documentObserver?.disconnect();
            this.#documentObserver = null;
            if (this.#refreshFrame) {
                cancelAnimationFrame(this.#refreshFrame);
                this.#refreshFrame = 0;
            }
            if (this.#initRetryTimeout) {
                clearTimeout(this.#initRetryTimeout);
                this.#initRetryTimeout = 0;
            }
            this.#initCalledForThisElement = false;
            this.#sliderRevealed = false;
            this.#lastSliderTrack = null;
            this.#lastSliderCardCount = -1;
            this.#lastSliderArrows = [];
            this.#lastSliderToggles = [];
        }

        /**
         * Rebuy global init scans for [data-rebuy-id] mounts (no widget id arg).
         * @param {string} widgetId
         * @param {boolean} mountNeedsInit
         * @returns {boolean}
         */
        #invokeRebuyInit(widgetId, mountNeedsInit) {
            const rebuy = window.Rebuy;
            if (!rebuy) return false;

            if (typeof rebuy.init === 'function') {
                rebuy.init();
                return true;
            }

            const widgets = rebuy.widgets;
            if (!Array.isArray(widgets)) return false;

            const widget = widgets.find((entry) => String(entry?.id) === String(widgetId));
            if (!widget) return false;

            if (mountNeedsInit && typeof widget.render === 'function') {
                widget.render();
                return true;
            }

            if (typeof widget.getWidgetProducts === 'function') {
                widget.getWidgetProducts();
                return true;
            }

            return false;
        }

        initRebuy () {
            const widgetId = this.dataset.rebuyWidgetId;
            if (!widgetId) return;

            const mountNeedsInit = this.#rebuyMountNeedsInit(widgetId);
            if (this.#initCalledForThisElement && !mountNeedsInit) return;

            if (this.#invokeRebuyInit(widgetId, mountNeedsInit)) {
                this.#initCalledForThisElement = true;
                return;
            }

            // Rebuy app script may load after cart-drawer:open on fast add-to-cart.
            if (this.#initRetryTimeout) return;
            this.#initRetryTimeout = window.setTimeout(() => {
                this.#initRetryTimeout = 0;
                if (!this.isConnected) return;
                if (this.#invokeRebuyInit(widgetId, this.#rebuyMountNeedsInit(widgetId))) {
                    this.#initCalledForThisElement = true;
                }
            }, 500);
        }

        initRebuySlider () {
            const rebuyWidgetBlock = document.querySelector('.rebuy-product-slider-block[data-rebuy-widget-id="' + this.dataset.rebuyWidgetId + '"]');
            if (!rebuyWidgetBlock) {
                return;
            }
            const REBUY_SELECTOR = `[data-rebuy-id="${this.dataset.rebuyWidgetId}"]`;
            const productCards = document.querySelectorAll(REBUY_SELECTOR + ' .product-card').length;
            if (productCards > 0) {
                rebuyWidgetBlock.classList.remove('hidden');
                this.#sliderRevealed = true;
            }
        }

        tryInitRebuySlider () {
            const REBUY_SELECTOR = `[data-rebuy-id="${this.dataset.rebuyWidgetId}"]`;
            const SLIDER_SELECTOR = '[data-cx-card-slider="true"]';
            const container = document.querySelector(REBUY_SELECTOR);
            const slider = container?.querySelector(SLIDER_SELECTOR);
            const track = slider?.querySelector('.custom-container__cards');
            if (!(container instanceof Element) || !(slider instanceof Element) || !(track instanceof Element)) return;

            // Belt-and-braces alongside the 'rebuy.ready' listener below: this poll-driven path
            // (mutation/timeout/load) reveals the slider even if 'rebuy.ready' fires before this
            // element's listener is attached (e.g. once its script is loaded lazily). Gated on
            // #sliderRevealed so the extra DOM queries run at most once per instance instead of on
            // every one of the several guaranteed calls (immediate + mutation + 800ms timeout + load).
            // IW performance note: the former 200ms polling and fixed timeout are now replaced by
            // targeted DOM observers plus one animation-frame refresh per mutation burst.
            if (!this.#sliderRevealed && container.querySelectorAll('.product-card').length > 0) {
                const rebuyWidgetBlock = document.querySelector('.rebuy-product-slider-block[data-rebuy-widget-id="' + this.dataset.rebuyWidgetId + '"]');
                if (rebuyWidgetBlock) rebuyWidgetBlock.classList.remove('hidden');
                this.#sliderRevealed = true;
            }

            const cardCount = track.querySelectorAll('.product-card, .custom-container__card').length;
            const arrows = Array.from(slider.querySelectorAll('.custom-container__arrow'));
            const toggles = Array.from(slider.querySelectorAll('[data-cx-mobile-grid-toggle="true"]'));
            if (cardCount === 0) return;

            const arrowsUnchanged =
                arrows.length === this.#lastSliderArrows.length &&
                arrows.every((arrow, index) => arrow === this.#lastSliderArrows[index]);
            const togglesUnchanged =
                toggles.length === this.#lastSliderToggles.length &&
                toggles.every((toggle, index) => toggle === this.#lastSliderToggles[index]);
            if (
                track === this.#lastSliderTrack &&
                cardCount === this.#lastSliderCardCount &&
                arrowsUnchanged &&
                togglesUnchanged
            ) {
                return;
            }

            this.#lastSliderTrack = track;
            this.#lastSliderCardCount = cardCount;
            this.#lastSliderArrows = arrows;
            this.#lastSliderToggles = toggles;

            if (typeof document.dispatchEvent !== 'function') return;
            try {
                document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { target: container } }));
            } catch (e) {}
        }

        scheduleRefresh () {
            if (this.#refreshFrame) return;
            this.#refreshFrame = requestAnimationFrame(() => {
                this.#refreshFrame = 0;
                if (!this.isConnected) return;
                this.initRebuySlider();
                this.tryInitRebuySlider();
            });
        }

        /** @param {Element} container */
        #observeContainer(container) {
            this.#documentObserver?.disconnect();
            this.#documentObserver = null;
            this.#containerObserver?.disconnect();
            this.#containerObserver = null;
            this.#lastSliderTrack = null;
            this.#lastSliderCardCount = -1;
            this.#lastSliderArrows = [];
            this.#lastSliderToggles = [];

            this.scheduleRefresh();

            if (typeof MutationObserver === 'function') {
                this.#containerObserver = new MutationObserver(this.scheduleRefresh);
                this.#containerObserver.observe(container, { childList: true, subtree: true });
            }

            if (document.readyState !== 'complete' && !this.#loadBound) {
                this.#loadBound = true;
                window.addEventListener('load', this.scheduleRefresh, { once: true });
            }
        }

        observeRebuy() {
            const REBUY_SELECTOR = `[data-rebuy-id="${this.dataset.rebuyWidgetId}"]`;
            const container = document.querySelector(REBUY_SELECTOR);

            if (container instanceof Element) {
                this.#observeContainer(container);
                return;
            }

            // IW performance note: observe insertion instead of polling every 200ms while the
            // Rebuy mount is absent, then disconnect as soon as the matching container appears.
            if (typeof MutationObserver !== 'function' || this.#documentObserver) return;
            this.#documentObserver = new MutationObserver(() => {
                const insertedContainer = document.querySelector(REBUY_SELECTOR);
                if (!(insertedContainer instanceof Element)) return;
                this.#observeContainer(insertedContainer);
            });
            this.#documentObserver.observe(document.documentElement, { childList: true, subtree: true });
        }
    });
})();
